import React, { Fragment } from 'react';

import Script from 'dangerous-html/react';
import { Helmet } from 'react-helmet';

import FooterBoton from '../components/footer-boton';
import FeatureSection from '../components/feature-section';
import FaqItem from '../components/faq-item';
import LogoCarousel from '../components/logo-carousel';
import './home.css';

const CALENDLY_URL = 'https://calendly.com/d/cscj-pcy-82x';

const Home = (props) => {
  return (
    <div className='home-container10'>
      <Helmet>
        <title>
          Botón | Contabilidad tercerizada con contadores partners en Panamá
        </title>
        <meta
          name='description'
          content='Terceriza tu contabilidad sin llevar un Excel paralelo. Botón es el software donde operas tu negocio, y te empareja con contadores CPA partners que trabajan sobre tus mismos números.'
        />
        <meta
          property='og:title'
          content='Botón | Contabilidad tercerizada con contadores partners en Panamá'
        />
        <meta
          property='og:description'
          content='Terceriza tu contabilidad sin llevar un Excel paralelo. Botón es el software donde operas tu negocio, y te empareja con contadores CPA partners que trabajan sobre tus mismos números.'
        />
        <meta
          property='og:image'
          content='https://botonapp.com/Images/demo-1200w.png'
        />

        {/* Additional Blog Links for SEO */}
        <link
          rel='alternate'
          type='application/rss+xml'
          title='Blog de Boton'
          href='https://botonapp.com/blog'
        />
        <link rel='canonical' href='https://botonapp.com' />

        {/* Structured Data for Organization */}
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Boton',
            alternateName: 'Hero',
            url: 'https://botonapp.com',
            logo: 'https://botonapp.com/boton_logo/boton-200h.png',
            sameAs: [
              'https://www.prensa.com/martes-financiero/panamena-hero-impulsada-por-y-combinator/',
            ],
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+507-6618-1519',
              contactType: 'customer service',
              availableLanguage: 'Spanish',
            },
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'PA',
            },
            description:
              'Botón es una compañía de software que empareja a PYMES en Panamá con contadores CPA partners y les da la plataforma donde ambos trabajan: cuentas por pagar, bancos y ventas al día.',
            mainEntity: {
              '@type': 'Blog',
              name: 'Blog de Boton',
              url: 'https://botonapp.com/blog',
              description:
                'Recursos y artículos sobre contabilidad para PYMES en Panamá.',
            },
          })}
        </script>

        {/* Structured Data for Software Application */}
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Boton',
            description:
              'Plataforma donde las PYMES en Panamá llevan su operación (cuentas por pagar, bancos, ventas) y donde trabaja su contador CPA partner. Botón no brinda servicios de contabilidad; el servicio lo brindan contadores partners.',
            applicationCategory: 'Accounting Software',
            operatingSystem: 'Web-based',
            url: 'https://botonapp.com',
            screenshot: 'https://botonapp.com/Images/demo-1200w.png',
            featureList: [
              'Cuentas por pagar al día',
              'Conexión con la DGI para facturas electrónicas',
              'Conexión con bancos panameños (Banco General, BAC)',
              'Integración con Invu POS y Shopify',
              'Captura de facturas por WhatsApp',
              'Conciliación bancaria diaria',
              'Emparejamiento con contadores CPA partners',
            ],
            targetAudience: {
              '@type': 'Audience',
              audienceType: 'PYMES en Panamá',
            },
          })}
        </script>

        {/* Structured Data for FAQ */}
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '¿Botón brinda el servicio de contabilidad?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'No. Botón es una compañía de tecnología: construimos el software donde llevas tu operación y donde trabaja tu contador. El servicio contable lo brindan contadores partners: CPAs independientes que tienen un acuerdo de nivel de servicio con Botón. Nosotros te emparejamos con el partner adecuado.',
                },
              },
              {
                '@type': 'Question',
                name: '¿Qué pasa con mi contador actual?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Depende de ti. Si tu contador quiere trabajar con Botón, puede aplicar para ser partner. Si prefieres, en la llamada te presentamos partners que ya dominan la plataforma y tú decides.',
                },
              },
              {
                '@type': 'Question',
                name: '¿Qué pasa si mi partner me queda mal?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Cada partner tiene un acuerdo de nivel de servicio con Botón. Si no cumple, puedes cambiar de partner. Tu información vive en Botón, así que el cambio no implica empezar de cero.',
                },
              },
              {
                '@type': 'Question',
                name: '¿Tengo que seguir llevando mi Excel interno?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Esa es la idea: que no. Llevas tus cuentas por pagar y tu operación en Botón, y tu contador trabaja sobre esos mismos números. La contabilidad paralela deja de tener sentido.',
                },
              },
              {
                '@type': 'Question',
                name: '¿Necesito usar Invu POS o Shopify?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'No. Ayudan porque las ventas entran solas, pero Botón también funciona para negocios de servicios y para tiendas y restaurantes con otros sistemas. La conexión con la DGI y con tus bancos aplica igual.',
                },
              },
              {
                '@type': 'Question',
                name: '¿Cuánto cuesta?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Depende del tamaño de tu negocio y del alcance del servicio con el partner. En la llamada te damos un número claro, sin vueltas.',
                },
              },
              {
                '@type': 'Question',
                name: '¿Qué pasa en la llamada?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Es una llamada breve con el equipo de Botón. Queremos saber a qué se dedica tu negocio, cómo está tu contabilidad hoy y qué te importa resolver. Con eso te proponemos el contador partner adecuado.',
                },
              },
            ],
          })}
        </script>
      </Helmet>
      <section className='home-hero'>
        <header className='home-header1'>
          <div id='mobile-menu' className='home-mobile-navigation'>
            <img
              alt='pastedImage'
              src='/boton_logo/boton%20black%20wide-1500h.png'
              className='home-logo1'
            />
            <div className='home-links1'>
              <a
                href='https://botonapp.com/login'
                target='_blank'
                rel='noreferrer noopener'
                className='home-login'
              >
                Login
              </a>
            </div>
            <div id='close-mobile-menu' className='home-close-mobile-menu'>
              <svg viewBox='0 0 804.5714285714286 1024' className='home-icon10'>
                <path d='M741.714 755.429c0 14.286-5.714 28.571-16 38.857l-77.714 77.714c-10.286 10.286-24.571 16-38.857 16s-28.571-5.714-38.857-16l-168-168-168 168c-10.286 10.286-24.571 16-38.857 16s-28.571-5.714-38.857-16l-77.714-77.714c-10.286-10.286-16-24.571-16-38.857s5.714-28.571 16-38.857l168-168-168-168c-10.286-10.286-16-24.571-16-38.857s5.714-28.571 16-38.857l77.714-77.714c10.286-10.286 24.571-16 38.857-16s28.571 5.714 38.857 16l168 168 168-168c10.286-10.286 24.571-16 38.857-16s28.571 5.714 38.857 16l77.714 77.714c10.286 10.286 16 24.571 16 38.857s-5.714 28.571-16 38.857l-168 168 168 168c10.286 10.286 16 24.571 16 38.857z'></path>
              </svg>
            </div>
          </div>
          <div className='home-desktop-navigation'>
            <nav className='home-centered'>
              <div className='home-left1'>
                <img
                  alt='pastedImage'
                  src='/boton_logo/boton-200h.png'
                  className='home-logo2'
                />
                <div className='home-links2'>
                  <a href='#howitworks' className='home-link10 Link'>
                    Cómo funciona
                  </a>
                  <a href='#faq' className='home-link11 Link'>
                    <span>Preguntas frecuentes</span>
                  </a>
                </div>
              </div>
              <div className='home-right1'>
                <a
                  href='https://botonapp.com/login'
                  target='_blank'
                  rel='noreferrer noopener'
                  className='home-sign-in Link'
                >
                  Log in
                </a>
                <a
                  href={CALENDLY_URL}
                  target='_blank'
                  rel='noreferrer noopener'
                  className='home-link12'
                >
                  <div className='home-get-started1'>
                    <span className='home-text102'>Agendar llamada</span>
                  </div>
                </a>
                <div id='open-mobile-menu' className='home-burger-menu'>
                  <img
                    alt='pastedImage'
                    src='/pastedimage-yxbd.svg'
                    className='home-mobile-menu-button'
                  />
                </div>
              </div>
            </nav>
          </div>
          <div>
            <div className='home-container12'>
              <Script
                html={`<script defer>
/*
Mobile menu - Code Embed
*/

// Mobile menu
const mobileMenu = document.querySelector("#mobile-menu")

// Buttons
const closeButton = document.querySelector("#close-mobile-menu")
const openButton = document.querySelector("#open-mobile-menu")

// On openButton click, set the mobileMenu position left to -100vw
openButton.addEventListener("click", function() {
    mobileMenu.style.transform = "translateX(0%)"
})

// On closeButton click, set the mobileMenu position to 0vw
closeButton.addEventListener("click", function() {
    mobileMenu.style.transform = "translateX(100%)"
})
</script>`}
              ></Script>
            </div>
          </div>
        </header>
        <header className='home-content1'>
          <h1 className='home-text103'>
            Terceriza tu contabilidad. Olvídate del Excel paralelo.
          </h1>
          <p className='home-text104'>
            El software de Botón, más un contador partner con el que te
            emparejamos, te resuelven la contabilidad completa de tu negocio.
          </p>
          <div className='home-container13'>
            <a
              href={CALENDLY_URL}
              target='_blank'
              rel='noreferrer noopener'
              className='home-link13'
            >
              <div className='home-get-started2'>
                <span className='home-text105'>Agendar una llamada breve</span>
              </div>
            </a>
          </div>
        </header>
        <div className='home-dashboard-preview'>
          <div className='home-outline'>
            <img
              src='/Images/landingpage.png'
              alt='Plataforma de Botón: cuentas por pagar, bancos y ventas al día'
              className='home-video'
              title='Plataforma de Botón'
            />
          </div>
        </div>
      </section>

      <LogoCarousel />

      {/* El problema */}
      <FeatureSection
        variant='grey'
        layout='centered'
        subtitle='El problema'
        title='Tercerizas la contabilidad y aun así llevas otra por tu cuenta'
        description={
          <>
            Es la historia de siempre: contratas un contador externo, pero
            igual terminas llevando un Excel interno para saber cómo va el
            negocio. El contador registra lo que ya pasó en un software que
            solo él usa, y te manda cortes con semanas de atraso. Esos números
            del pasado no te sirven para decidir hoy.
            <br />
            <br />
            Encima, el orden de los documentos corre por tu cuenta: mantener el
            Drive al día, perseguir facturas, responder correos. Mucho trabajo
            a cambio de poco valor.
            <br />
            <br />
            Botón lo resuelve con tecnología. Llevas tus cuentas por pagar y
            tu operación del día a día en el software de Botón, y tu contador
            partner trabaja sobre esa misma información, acompañándote. La
            contabilidad paralela desaparece.
          </>
        }
      />

      <section id='howitworks' className='home-how-it-works'>
        <div className='home-centered-container3'>
          <div className='home-heading2'>
            <span className='home-text251'>¿Cómo funciona?</span>
            <span className='home-text252 title'>Tres pasos</span>
          </div>
          <div className='home-steps'>
            <div className='home-step'>
              <span className='home-step-number'>1</span>
              <h3 className='home-step-title'>Agenda una llamada breve</h3>
              <p className='home-step-text'>
                Nos cuentas a qué se dedica tu negocio, cómo está tu
                contabilidad hoy y qué te importa resolver. Con eso sabemos
                qué contador partner te conviene.
              </p>
            </div>
            <div className='home-step'>
              <span className='home-step-number'>2</span>
              <h3 className='home-step-title'>
                Escoge a tu contador partner
              </h3>
              <p className='home-step-text'>
                Te presentamos contadores partners que encajan con tu negocio:
                CPAs con buena reputación, buen servicio y dominio de Botón.
                Tú escoges.
              </p>
            </div>
            <div className='home-step'>
              <span className='home-step-number'>3</span>
              <h3 className='home-step-title'>
                Opera en Botón, con tu contador al lado
              </h3>
              <p className='home-step-text'>
                Llevas tus cuentas por pagar, bancos y ventas en Botón. Tu
                contador trabaja sobre esos mismos números y te acompaña. Nada
                de cortes atrasados ni Excel paralelo.
              </p>
            </div>
          </div>
          <a
            href={CALENDLY_URL}
            target='_blank'
            rel='noreferrer noopener'
            className='home-steps-cta'
          >
            <div className='template-button'>
              <span className='home-text257'>Agendar llamada</span>
            </div>
          </a>
        </div>
      </section>

      {/* Para quién */}
      <section className='home-case-studies'>
        <div className='home-centered-container-case-studies'>
          <div className='home-heading-case-studies'>
            <span className='sub-title'>Para quién</span>
            <h2 className='title'>Hecho para PYMES en Panamá</h2>
          </div>
          <div className='home-case-studies-grid'>
            <div className='home-case-study-card'>
              <div className='home-case-study-header'>
                <h3 className='home-case-study-title'>Restaurantes</h3>
              </div>
              <p className='home-case-study-description'>
                Si usas Invu POS, tus ventas entran solas a Botón. Controlas
                costo de comida, planilla y gastos por sucursal, y pagas
                proveedores sabiendo cuánta plata tienes.
              </p>
              <div className='home-case-study-results'>
                <div className='home-case-study-result-item'>
                  <span>Integración con Invu POS</span>
                </div>
                <div className='home-case-study-result-item'>
                  <span>Costos y gastos por sucursal</span>
                </div>
                <div className='home-case-study-result-item'>
                  <span>Cuentas por pagar a proveedores, al día</span>
                </div>
              </div>
            </div>

            <div className='home-case-study-card'>
              <div className='home-case-study-header'>
                <h3 className='home-case-study-title'>Tiendas</h3>
              </div>
              <p className='home-case-study-description'>
                Si vendes con Shopify, Botón registra tus ventas
                automáticamente. Ves tu margen real por producto y manejas las
                compras de inventario sin sorpresas de caja.
              </p>
              <div className='home-case-study-results'>
                <div className='home-case-study-result-item'>
                  <span>Integración con Shopify</span>
                </div>
                <div className='home-case-study-result-item'>
                  <span>Margen real por producto</span>
                </div>
                <div className='home-case-study-result-item'>
                  <span>Compras e inventario bajo control</span>
                </div>
              </div>
            </div>

            <div className='home-case-study-card'>
              <div className='home-case-study-header'>
                <h3 className='home-case-study-title'>Servicios</h3>
              </div>
              <p className='home-case-study-description'>
                Facturas, cobras y pagas desde un solo lugar. Sabes quién te
                debe, a quién le debes y cuánto te queda a fin de mes.
              </p>
              <div className='home-case-study-results'>
                <div className='home-case-study-result-item'>
                  <span>Cuentas por cobrar y por pagar</span>
                </div>
                <div className='home-case-study-result-item'>
                  <span>Facturación electrónica con la DGI</span>
                </div>
                <div className='home-case-study-result-item'>
                  <span>Números claros por cliente o proyecto</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='home-banners'>
        <FeatureSection
          variant='white'
          layout='left-right'
          subtitle='Cuentas por pagar'
          title='Tus cuentas por pagar, al día y en un solo lugar'
          description={
            <>
              Las facturas de tus proveedores llegan a Botón directo desde la
              DGI. Ves qué debes, a quién y cuándo vence, y pagas sabiendo tu
              posición real de efectivo, porque Botón también se conecta a tus
              bancos.
              <br />
              <br />
              Esta es la contabilidad operativa de la que sí puedes depender.
              Tu contador partner trabaja sobre ella, no aparte de ella.
            </>
          }
          buttonText='Agendar llamada'
          buttonHref={CALENDLY_URL}
          imageSrc='/Images/contabilidad%20correcta-1200w.png'
          imageAlt='Cuentas por pagar por proveedor en Botón'
        />

        <FeatureSection
          variant='grey'
          layout='right-left'
          subtitle='Documentos'
          title='Se acabó el Drive de facturas'
          description={
            <>
              Manda una foto de la factura por WhatsApp y listo. Botón la
              procesa, la guarda y la deja lista para tu contador.
              <br />
              <br />
              Nadie te va a volver a pedir que ordenes documentos a fin de mes.
            </>
          }
          imageSrc='/Images/bot_whatsapp.png'
          imageAlt='Bot de WhatsApp procesando facturas'
        />

        <FeatureSection
          variant='white'
          layout='left-right'
          subtitle='Números al día'
          title='Conciliación automática, todos los días'
          description={
            <>
              Botón cruza tus bancos con tus facturas y ventas diariamente. No
              esperas al cierre de mes para saber cómo va el negocio: los
              números están ahí cuando los necesitas, y tu contador los revisa
              contigo.
            </>
          }
          imageSrc='/Images/conciliacion3.png'
          imageAlt='Conciliación automática en Botón'
        />
      </section>

      <section className='home-features-conecta-tus-fuentes'>
        <div className='home-title'>
          <span className='home-text365'>Conecta tus fuentes</span>
          <span className='home-text366'>
            <span>
              BOTON se actualizará
              <span
                dangerouslySetInnerHTML={{
                  __html: ' ',
                }}
              />
            </span>
            <span className='home-text368'>diariamente</span>
            <span> con la información nueva de tu negocio.</span>
          </span>
        </div>
        <div className='home-cards2'>
          <div className='home-container21'>
            <div className='home-schedule card'>
              <div className='home-container22'>
                <img
                  alt='pastedImage'
                  src='/sources/dgi-200h.png'
                  className='home-icon68'
                />
              </div>
              <span className='home-text370'>
                Dirección General de Ingresos
              </span>
              <span className='home-text371'>
                Para facturas electrónicas emitidas y recibidas.
              </span>
            </div>
            <div className='home-publish card'>
              <div className='home-container23'>
                <img
                  alt='pastedImage'
                  src='/sources/bg-200h.png'
                  className='home-icon69'
                />
                <img
                  alt='pastedImage'
                  src='/sources/bac-200h.png'
                  className='home-icon70'
                />
              </div>
              <span className='home-text372'>Bancos y tarjetas</span>
              <span className='home-text373'>
                Para estados de cuenta bancarios. Otros bancos también.
              </span>
            </div>
          </div>
          <div className='home-container24'>
            <div className='home-analyze card'>
              <div className='home-container25'>
                <img
                  alt='pastedImage'
                  src='/sources/mupa-200w.png'
                  className='home-icon71'
                />
                <img
                  alt='pastedImage'
                  src='/sources/css-200h.png'
                  className='home-icon72'
                />
              </div>
              <span className='home-text374'>Municipio y CSS</span>
              <span className='home-text375'>
                Para obligaciones de tu negocio.
              </span>
            </div>
            <div className='home-get-leads card'>
              <div className='home-container26'>
                <img
                  alt='pastedImage'
                  src='/sources/invuposnew-200h.jpg'
                  className='home-icon73'
                />
              </div>
              <span className='home-text376'>Sistemas de facturación</span>
              <a
                href='http://invupos.com/'
                target='_blank'
                rel='noreferrer noopener'
                className='home-link22'
              >
                <span>
                  Para formas de pago.
                  <span
                    dangerouslySetInnerHTML={{
                      __html: ' ',
                    }}
                  />
                </span>
                <span>
                  Otros sistemas pronto.
                  <span
                    dangerouslySetInnerHTML={{
                      __html: ' ',
                    }}
                  />
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contadores partners */}
      <FeatureSection
        variant='grey'
        layout='left-right'
        subtitle='Contadores partners'
        title='CPAs curados, con nivel de servicio garantizado'
        description={
          <>
            <strong>
              Botón es una compañía de software, no una firma contable.
            </strong>{' '}
            El servicio de contabilidad lo brindan contadores partners: CPAs
            independientes que seleccionamos por su reputación, conocimiento,
            profesionalismo y dominio de Botón.
            <br />
            <br />
            Tu partner, usando Botón, te resuelve todas las necesidades
            contables del negocio:
            <ul className='feature-section-list'>
              <li>Formulario 430 de ITBMS</li>
              <li>Cálculo de planilla, presentación de SIPE y Planilla 03</li>
              <li>Cálculo de liquidaciones</li>
              <li>Estados financieros</li>
              <li>Declaraciones de renta</li>
              <li>Informe 43 de Compras</li>
            </ul>
            <br />
            Cada partner firma un acuerdo de nivel de servicio con Botón. Si tu
            partner no cumple, puedes cambiar de partner sin empezar de cero:
            tu información sigue en Botón.
          </>
        }
        buttonText='Agendar llamada'
        buttonHref={CALENDLY_URL}
        imageSrc='/Images/los%20mejores1-1200w.png'
        imageAlt='Contadores CPA partners de Botón'
      >
        <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
          ¿Eres CPA y quieres ser partner?{' '}
          <a
            href='https://api.whatsapp.com/send?phone=66181519&text=Hola.%20Soy%20CPA%20y%20quiero%20ser%20partner%20de%20BOTON'
            target='_blank'
            rel='noreferrer noopener'
          >
            Escríbenos por WhatsApp
          </a>
          .
        </p>
      </FeatureSection>

      <section id='faq' className='home-faq'>
        <div className='home-heading1'>
          <span className='home-text165 title'>Preguntas Frecuentes</span>
        </div>
        <div className='home-accordion1'>
          <FaqItem
            question='¿Botón brinda el servicio de contabilidad?'
            answer={
              <>
                No. Botón es una compañía de tecnología: construimos el
                software donde llevas tu operación y donde trabaja tu
                contador. El servicio contable lo brindan contadores partners:{' '}
                <strong>CPAs independientes</strong> que tienen un acuerdo de
                nivel de servicio con Botón. Nosotros te emparejamos con el
                partner adecuado.
              </>
            }
            isOpen={true}
          />
          <FaqItem
            question='¿Qué pasa con mi contador actual?'
            answer={
              <>
                Depende de ti. Si tu contador quiere trabajar con Botón, puede
                aplicar para ser partner. Si prefieres, en la llamada te
                presentamos partners que ya dominan la plataforma y tú decides.
              </>
            }
          />
          <FaqItem
            question='¿Qué pasa si mi partner me queda mal?'
            answer={
              <>
                Cada partner tiene un{' '}
                <strong>acuerdo de nivel de servicio</strong> con Botón. Si no
                cumple, puedes cambiar de partner. Tu información vive en
                Botón, así que el cambio no implica empezar de cero.
              </>
            }
          />
          <FaqItem
            question='¿Tengo que seguir llevando mi Excel interno?'
            answer={
              <>
                Esa es la idea: que no. Llevas tus cuentas por pagar y tu
                operación en Botón, y tu contador trabaja sobre esos mismos
                números. La contabilidad paralela deja de tener sentido.
              </>
            }
          />
          <FaqItem
            question='¿Necesito usar Invu POS o Shopify?'
            answer={
              <>
                No. Ayudan porque las ventas entran solas, pero Botón también
                funciona para negocios de servicios y para tiendas y
                restaurantes con otros sistemas. La conexión con la DGI y con
                tus bancos aplica igual.
              </>
            }
          />
          <FaqItem
            question='¿Cuánto cuesta?'
            answer={
              <>
                Depende del tamaño de tu negocio y del alcance del servicio con
                el partner. En la llamada te damos un número claro, sin
                vueltas.
              </>
            }
          />
          <FaqItem
            question='¿Qué pasa en la llamada?'
            answer={
              <>
                Es una llamada breve con el equipo de Botón, no una venta a
                presión. Queremos saber a qué se dedica tu negocio, cómo está
                tu contabilidad hoy y qué te importa resolver. Con eso te
                proponemos el contador partner adecuado.
              </>
            }
          />
        </div>
        <div>
          <div className='home-container18'>
            <Script
              html={`<script>
// Accordion - Code Embed

const accordionContainers = document.querySelectorAll(".accordionContainer"); // All accordion containers
const accordionContents = document.querySelectorAll(".accordionContent"); // All accordion content
const accordionIcons = document.querySelectorAll(".accordionIcon"); // All accordion icons

accordionContents.forEach((accordionContent) => {
    accordionContent.style.display = "none"; //Hides all accordion contents
});

accordionContainers.forEach((accordionContainer, index) => {
    accordionContainer.addEventListener("click", () => {
        accordionContents.forEach((accordionContent) => {
            accordionContent.style.display = "none"; //Hides all accordion contents
        });

        accordionIcons.forEach((accordionIcon) => {
            accordionIcon.style.transform = "rotate(0deg)"; // Resets all icon transforms to 0deg (default)
        });

        if (accordionContainer.classList.contains("accordion-open")) { // Checks if this container has class "accordion-open"
            accordionContainer.classList.remove("accordion-open"); // Removes class "accordion-open"
        } else {
            accordionContainers.forEach((accordionContainer) => {
                accordionContainer.classList.remove("accordion-open"); // Removes class "accordion-open" from all containers
            });

            accordionContainer.classList.add("accordion-open"); // Adds class "accordion-open" to this container
            accordionContents[index].style.display = "flex"; // Shows accordion content
            accordionIcons[index].style.transform = "rotate(180deg)"; // Rotates accordion icon 180deg
        }
    });
});
</script>`}
            ></Script>
          </div>
        </div>
      </section>

      <section className='home-demo2'>
        <div className='home-action'>
          <div className='home-heading4'>
            <span className='home-text360 title'>
              Agenda una llamada breve
            </span>
            <span className='home-text361'>
              En 20 minutos entendemos tu negocio, el estatus de tu
              contabilidad y qué te importa resolver. Con eso te proponemos el
              contador partner adecuado.
            </span>
            <a
              href={CALENDLY_URL}
              target='_blank'
              rel='noreferrer noopener'
              className='home-link21'
            >
              <div className='home-get-started9 template-button'>
                <span className='home-text362'>
                  <span>Agendar</span>
                  <br></br>
                </span>
              </div>
            </a>
          </div>
          <div className='home-images'>
            <img
              alt='image'
              src='/Images/demo-1200w.png'
              className='home-image3'
            />
          </div>
        </div>
      </section>

      <FooterBoton
        precios=''
        precios111=''
        noticias={
          <Fragment>
            <a
              href='https://www.prensa.com/martes-financiero/panamena-hero-impulsada-por-y-combinator/'
              target='_blank'
              rel='noreferrer noopener'
              className='home-noticias1'
            >
              <span>En las noticias</span>
            </a>
          </Fragment>
        }
        noticias111={
          <Fragment>
            <a
              href='https://www.prensa.com/martes-financiero/panamena-hero-impulsada-por-y-combinator/'
              target='_blank'
              rel='noreferrer noopener'
              className='home-noticias2'
            >
              <span>En las noticias</span>
            </a>
          </Fragment>
        }
        rootClassName='footer-botonroot-class-name2'
        trabajaConNosotros1={
          <Fragment>
            <a
              href='https://api.whatsapp.com/send?phone=66181519'
              target='_blank'
              rel='noreferrer noopener'
              className='home-trabaja-con-nosotros1'
            >
              Trabaja en BOTON
            </a>
          </Fragment>
        }
        trabajaConNosotros12={
          <Fragment>
            <a
              href='https://api.whatsapp.com/send?phone=66181519'
              target='_blank'
              rel='noreferrer noopener'
              className='home-trabaja-con-nosotros2'
            >
              Formerly My Task Hero, Inc.
            </a>
          </Fragment>
        }
        trabajaConNosotros1111={
          <Fragment>
            <a
              href='https://api.whatsapp.com/send?phone=66181519'
              target='_blank'
              rel='noreferrer noopener'
              className='home-trabaja-con-nosotros3'
            >
              <span>Trabaja en Hero</span>
            </a>
          </Fragment>
        }
      ></FooterBoton>
    </div>
  );
};

export default Home;
